import { useSearchParams } from 'react-router-dom';
import { Temporal } from 'temporal-polyfill';

function parseDateParam(value: string | null): Temporal.PlainDate | undefined {
  if (!value) return undefined;
  try {
    return Temporal.PlainDate.from(value);
  } catch {
    return undefined;
  }
}

/**
 * URL-backed (`?date=`) selected date for the Calendar page — two-way: reads the initial
 * date from the URL, and `setSelectedDate` is meant to be wired to `CalendarView`'s
 * `onSelectedDateUpdate` so prev/next/today navigation writes back too, keeping the URL
 * shareable at whatever date the calendar is currently showing.
 */
export function useCalendarDate() {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedDate = parseDateParam(searchParams.get('date'));

  const setSelectedDate = (date: Temporal.PlainDate) => {
    // No-op on the value already reflected in the URL (or, if the URL has no `date` yet,
    // on today's date — the implicit default) so that CalendarView's initial-mount firing
    // of onSelectedDateUpdate doesn't trigger an immediate, needless remount via the `key`
    // callers derive from selectedDate.
    if (selectedDate ? date.equals(selectedDate) : date.equals(Temporal.Now.plainDateISO())) {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('date', date.toString());
    setSearchParams(nextParams, { replace: true });
  };

  return { selectedDate, setSelectedDate };
}
